import { useState, useEffect, useRef, useCallback } from "react";

const STATS = [
  { value: "15", label: "엄선된 숙소" },
  { value: "4.96", label: "평균 평점" },
  { value: "1,200+", label: "누적 투숙객" },
  { value: "100%", label: "한국어 소통" },
];

const BG_IMAGES = [
  {
    src: "https://public.readdy.ai/ai/img_res/2e6a3abc8b16fa4f92ed12854f32df51.jpg",
    alt: "후쿠오카 하카타만 석양",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Fukuoka%20tower%20Momochi%20seaside%20park%20night%20illumination%20glowing%20landmark%20with%20city%20lights%20reflection%20beautiful%20Japan%20travel%20destination%20vibrant%20neon&width=1600&height=900&seq=2002&orientation=landscape",
    alt: "후쿠오카 타워 야경",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Nakasu%20Fukuoka%20night%20yatai%20food%20stalls%20lanterns%20glowing%20river%20reflections%20neon%20signs%20lively%20atmosphere%20Japan%20street%20food%20scene&width=1600&height=900&seq=2003&orientation=landscape",
    alt: "나카스 야타이 야경",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Tenjin%20Fukuoka%20busy%20shopping%20street%20daytime%20pedestrians%20fashionable%20stores%20lively%20urban%20scene%20Japan%20city%20life%20vibrant%20colorful%20storefronts&width=1600&height=900&seq=2004&orientation=landscape",
    alt: "텐진 거리",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Ohori%20park%20Fukuoka%20Japan%20beautiful%20lake%20sunrise%20morning%20mist%20peaceful%20walking%20path%20lush%20green%20trees%20reflection%20calm%20serene%20atmosphere&width=1600&height=900&seq=2005&orientation=landscape",
    alt: "오호리 공원",
  },
];

const SLIDE_INTERVAL = 5000;

export default function HeroSection() {
  const [visible, setVisible] = useState(false);
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const currentRef = useRef(current);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const goTo = useCallback((next: number) => {
    setFading(true);
    setTimeout(() => {
      setCurrent(next);
      setFading(false);
    }, 350);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const next = (currentRef.current + 1) % BG_IMAGES.length;
      goTo(next);
    }, SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, [goTo]);

  const handleDot = (i: number) => {
    if (i === currentRef.current || fading) return;
    goTo(i);
  };

  const handlePrev = () => {
    if (fading) return;
    goTo((currentRef.current - 1 + BG_IMAGES.length) % BG_IMAGES.length);
  };

  const handleNext = () => {
    if (fading) return;
    goTo((currentRef.current + 1) % BG_IMAGES.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx < 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background Slides */}
      {BG_IMAGES.map((img, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-500"
          style={{
            opacity: i === current ? (fading ? 0 : 1) : 0,
            zIndex: i === current ? 1 : 0,
            pointerEvents: "none",
          }}
        >
          <img
            src={img.src}
            alt={img.alt}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/45 to-black/85"></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>
      ))}

      {/* Arrow Buttons - PC only */}
      <button
        onClick={handlePrev}
        className="hidden sm:flex absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center bg-black/30 hover:bg-black/55 backdrop-blur-sm border border-white/20 rounded-full transition-all cursor-pointer"
        aria-label="이전 슬라이드"
      >
        <i className="ri-arrow-left-s-line text-white text-xl"></i>
      </button>
      <button
        onClick={handleNext}
        className="hidden sm:flex absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center bg-black/30 hover:bg-black/55 backdrop-blur-sm border border-white/20 rounded-full transition-all cursor-pointer"
        aria-label="다음 슬라이드"
      >
        <i className="ri-arrow-right-s-line text-white text-xl"></i>
      </button>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-28 sm:pt-32">
        <div
          className={`transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-black/30 backdrop-blur-sm border border-amber-400/40 text-amber-300 text-xs font-medium px-3 py-1.5 sm:px-4 sm:py-2 rounded-full mb-4 sm:mb-6">
            <i className="ri-map-pin-line"></i>
            후쿠오카 · 한국인 호스트 큐레이션
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-3 sm:mb-4 max-w-4xl [text-shadow:0_2px_12px_rgba(0,0,0,0.6)]">
            한국인 호스트가<br />
            <span className="text-amber-400">반겨주는</span><br />
            후쿠오카 숙소
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 max-w-xl leading-relaxed [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]">
            일본어 걱정 NO. 예약부터 퇴실까지<br />
            카카오톡 한국어 소통 완벽 지원 숙소 Top 15
          </p>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mb-10 sm:mb-14">
            <button
              onClick={() => handleScroll("#best15")}
              className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 sm:px-7 sm:py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-building-2-line"></i>
              숙소 15곳 보기
            </button>
            <button
              onClick={() => handleScroll("#host-story")}
              className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/30 text-white font-medium px-6 py-3 sm:px-7 sm:py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-user-heart-line"></i>
              호스트 알아보기
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:gap-10">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-lg sm:text-2xl md:text-3xl font-bold text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.6)]">{stat.value}</div>
                <div className="text-[10px] sm:text-xs text-white/70 mt-0.5 [text-shadow:0_1px_6px_rgba(0,0,0,0.5)]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {BG_IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => handleDot(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current
                ? "w-5 h-1.5 sm:w-6 sm:h-2 bg-amber-400"
                : "w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      {/* Slide Caption - 모바일에서 숨김 */}
      <div className="hidden sm:block absolute bottom-16 right-8 z-10">
        <span className="text-white/40 text-xs bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full">
          {BG_IMAGES[current].alt}
        </span>
      </div>
    </section>
  );
}
